/**
 * Browser-unload failsafe for in-flight Razorpay checkout abandonment.
 *
 * This is checkout infrastructure, not report infrastructure. The caller owns
 * the product-specific abandon payload; this module only guarantees that an
 * unresolved checkout gets one last chance to log when the page unloads.
 *
 * A hidden document is deliberately NOT an abandonment signal. Mobile payment
 * flows hide the page while Razorpay hands control to a UPI app, and tab/app
 * switches do the same while checkout remains active. Treating
 * `visibilitychange` as terminal records false abandons exactly when network
 * connectivity is transitioning, producing spurious AbandonPaymentFailed
 * errors and prematurely resolving the real unload fallback.
 */

type AbandonHandler = () => void;

interface InFlightCheckout {
  orderId: string;
  fireAbandon: AbandonHandler;
  resolved: boolean;
}

const inFlight = new Map<string, InFlightCheckout>();
let listenersInstalled = false;

export function markCheckoutInFlight(args: { orderId: string; fireAbandon: AbandonHandler }): void {
  if (typeof window === "undefined") return;
  inFlight.set(args.orderId, {
    orderId: args.orderId,
    fireAbandon: args.fireAbandon,
    resolved: false,
  });
  installListenersOnce();
}

export function resolveCheckout(orderId: string): void {
  if (typeof window === "undefined") return;
  const entry = inFlight.get(orderId);
  if (!entry) return;
  entry.resolved = true;
  inFlight.delete(orderId);
}

export function __resetForTests(): void {
  inFlight.clear();
}

function installListenersOnce(): void {
  if (listenersInstalled) return;
  if (typeof window === "undefined") return;
  window.addEventListener("beforeunload", flushAll);
  window.addEventListener("pagehide", flushAll);
  listenersInstalled = true;
}

function flushAll(): void {
  for (const entry of inFlight.values()) {
    if (entry.resolved) continue;
    try {
      entry.fireAbandon();
    } catch {
      // Browser analytics must never block the product.
    }
    entry.resolved = true;
  }
}
